import { useEffect, useMemo, useRef } from 'react';

import { graphql } from '@/__generated__';
import { AccountSearchQuery, AccountSearchQueryFilter } from '@/__generated__/graphql';
import { usePaginatedQuery } from '@/reducers/usePaginatedQuery';
import { truthy } from '@/util/array';
import { useLazyQuery } from '../useLazyQuery';

export const GetAccountSearchResults = graphql(/* GraphQL */ `
  query AccountSearch(
    $accountId: BigInt!
    $filter: AccountSearchQueryFilter!
    $first: Int!
    $after: String
  ) {
    catalog(accountId: $accountId) {
      search(first: $first, filter: $filter, after: $after) {
        totalCount
        pageInfo {
          endCursor
          hasNextPage
        }
        edges {
          node {
            hit {
              warehouseAsset {
                accountId
                credentialsId
                name
                relation
                urn
                warehouseType
                type
                connectionName
              }
              dbtAsset {
                resourceType
                __typename
                environmentId
                environmentDeploymentType
                projectId
                cloudProjectName
                uniqueId
                ... on ModelSearchNode {
                  modelHealthIssues: healthIssues
                  executeCompletedAt
                  fqn
                  __typename
                }
                ... on SourceSearchNode {
                  sourceHealthIssues: healthIssues
                  snapshottedAt
                  fqn
                  __typename
                }
                ... on ExposureSearchNode {
                  exposureHealthIssues: healthIssues
                  __typename
                }
                ... on SnapshotSearchNode {
                  executeCompletedAt
                  fqn
                  __typename
                }
                ... on SeedSearchNode {
                  executeCompletedAt
                  fqn
                  __typename
                }
                ... on TestSearchNode {
                  executeCompletedAt
                  fqn
                  __typename
                }
              }
              highlight
              matchedField
              resultName
              numCols
              __typename
            }
          }
        }
      }
    }
  }
`);

export type AppliedSearchParams = {
  accountId: number | undefined;
  filter: AccountSearchQueryFilter;
  pageSize?: number;
  after?: string;
  includeDebug?: boolean;
  pause?: boolean;
};

const getAccountSearchResults = (data: AccountSearchQuery) => {
  return data.catalog.search.edges.map((edge) => edge.node).filter(truthy);
};

const getAccountSearchPageInfo = (data: AccountSearchQuery) => {
  const { totalCount, pageInfo } = data.catalog.search ?? {
    totalCount: null,
    pageInfo: null,
  };
  return {
    totalCount: totalCount ?? null,
    endCursor: pageInfo?.endCursor ?? null,
    hasNextPage: pageInfo?.hasNextPage ?? false,
  };
};

export const useAccountSearchResults = ({
  accountId = 0,
  filter,
  pageSize = 50,
  after,
  pause,
}: AppliedSearchParams) => {
  const query = usePaginatedQuery(
    {
      query: GetAccountSearchResults,
      variables: {
        accountId,
        filter: {
          ...filter,
          resourceTypes: filter.resourceTypes?.length
            ? filter.resourceTypes
            : undefined,
        },
        first: pageSize,
        after,
      },
      pause: pause || !accountId || !filter.fields.length,
    },
    getAccountSearchPageInfo,
    getAccountSearchResults,
    () => null,
  );

  const isLoadingMore = query.fetching;

  // Track the filter that produced the current data so we can detect when
  // a new search has been submitted but results haven't arrived yet.
  const filterKey = JSON.stringify(filter);
  const loadedFilterKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!query.fetching && query.data != null) {
      loadedFilterKeyRef.current = filterKey;
    }
  }, [query.fetching, query.data, filterKey]);

  const isNewSearch = loadedFilterKeyRef.current !== filterKey;
  const isInitialLoad = query.fetching && (query.data == null || isNewSearch);

  return useMemo(() => {
    return {
      ...query,
      data:
        query.data && !isNewSearch
          ? query.data.map((d) => ({
              ...d,
              hit: {
                ...d.hit,
              },
            }))
          : undefined,
      isLoadingMore,
      isInitialLoad,
    };
  }, [query, isLoadingMore, isInitialLoad, isNewSearch]);
};

export const useLazyAccountSearchResults = () => {
  const [query, queryExecute] = useLazyQuery(GetAccountSearchResults);

  const executeQuery = async (args: AppliedSearchParams) => {
    const res = await queryExecute({
      accountId: args.accountId ?? 0,
      filter: args.filter,
      first: args.pageSize ?? 50,
      after: args.after ?? '',
    });
    return {
      ...res,
      data: res.data ? getAccountSearchResults(res.data) : [],
      pageInfo: res.data ? getAccountSearchPageInfo(res.data) : undefined,
      fetching: false,
    };
  };

  return [
    {
      ...query,
      data: query.data ? getAccountSearchResults(query.data) : [],
      pageInfo: query.data ? getAccountSearchPageInfo(query.data) : undefined,
    },
    executeQuery,
  ] as const;
};
